import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { loginUser, getIsLogin } from '@/utils/cookie';
import { useCampaign } from './useCampaign';
import { useSnVerification } from './useSnVerification';
import { useAddressSubmit } from './useAddressSubmit';
import { useMyMysteryBox } from './useMyMysteryBox';
import type { ModalType, AddressFormData } from './types';

/**
 * 服务焕新活动完整业务流程编排 hook
 *
 * 弹窗状态机：
 *   Join Now → [未登录] → login → verifySn → address → mysteryBox
 *   Join Now → [已登录] → verifySn → address → mysteryBox
 *   My Mystery Box → myMysteryBox（独立入口）
 *
 * SN 校验结果（sn + snUrl）在 flow hook 内部暂存，并在地址提交时透传给接口
 */
export type ServiceRenewalFlow = ReturnType<typeof useServiceRenewalFlow>;

export const useServiceRenewalFlow = () => {
    const { locale = 'us', query } = useRouter();
    const userChannel = (query?.source as string) === 'APP' ? 'APP' : 'Website';
    const [activeModal, setActiveModal] = useState<ModalType>(null);

    // 记录登录来源，登录成功后决定下一步弹窗
    const [loginSource, setLoginSource] = useState<'joinNow' | 'myMysteryBox'>('joinNow');
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        setIsLoggedIn(getIsLogin());
    }, []);

    // 暂存 SN 校验步骤的入参，供后续地址提交复用
    const [cachedSnData, setCachedSnData] = useState<{ sn: string; snUrl: string; deviceUrl: string } | null>(null);

    // 暂存地址表单数据，供盲盒弹窗提交时复用
    const [cachedAddressData, setCachedAddressData] = useState<AddressFormData | null>(null);

    const campaign = useCampaign(locale);
    const snVerification = useSnVerification();
    const addressSubmit = useAddressSubmit();
    const myMysteryBox = useMyMysteryBox();

    // ─── Dev Only: ?debugModal=xxx 直接打开指定弹窗 ──────────────────────────────
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        const modal = query?.debugModal as ModalType | undefined;
        if (modal) setActiveModal(modal);
    }, [query?.debugModal]);

    // 关闭弹窗时只清错误，不清 result（result 可能被下一个弹窗复用，例如 prizes）
    const closeModal = () => {
        setActiveModal(null);
        snVerification.resetError();
        addressSubmit.resetError();
    };

    // ─── Join Now 按钮点击 ───────────────────────────────────────────────────────

    const handleJoinNow = () => {
        const userInfo = loginUser();
        if (userInfo?.token) {
            setActiveModal('verifySn');
        } else {
            setLoginSource('joinNow');
            setActiveModal('login');
        }
    };

    // ─── 登录成功 ─────────────────────────────────────────────────────────────────

    const handleLoginSuccess = () => {
        setIsLoggedIn(true);
        closeModal();
        if (loginSource === 'myMysteryBox') {
            const userInfo = loginUser();
            const userId = userInfo?.user?.userId;
            myMysteryBox.fetchList({ page: 1, userId, campaignId: campaign.campaignId }, locale);
            setActiveModal('myMysteryBox');
        } else {
            setActiveModal('verifySn');
        }
    };

    // ─── SN 校验提交 ──────────────────────────────────────────────────────────────

    const handleSnVerified = async (sn: string, snUrl: string, deviceUrl: string) => {
        if (!campaign.campaignId) return;
        const result = await snVerification.verify({ sn, campaignId: campaign.campaignId, snUrl, deviceUrl }, locale);
        if (result) {
            if (result.isSecond) {
                closeModal();
                setActiveModal('snAlreadyRedeemed');
            } else {
                setCachedSnData({ sn, snUrl, deviceUrl });
                closeModal();
                setActiveModal('address');
            }
        }
    };

    // ─── 地址表单 Next Step（仅暂存，不提交 API） ────────────────────────────────

    const handleAddressSubmitted = (formData: AddressFormData) => {
        setCachedAddressData(formData);
        closeModal();
        setActiveModal('mysteryBox');
    };

    // ─── 盲盒弹窗：Open Mystery Boxes（真正提交 API） ──────────────────────────

    const handleOpenMysteryBox = async (recaptchaResponse: string): Promise<boolean> => {
        if (!campaign.campaignId || !cachedSnData || !cachedAddressData) return false;
        const result = await addressSubmit.submit(
            {
                ...cachedAddressData,
                sn: cachedSnData.sn,
                snUrl: cachedSnData.snUrl,
                deviceUrl: cachedSnData.deviceUrl,
                campaignId: campaign.campaignId,
                userChannel,
                recaptchaResponse,
            },
            locale,
        );
        return !!result;
    };

    // ─── My Mystery Box 按钮点击 ──────────────────────────────────────────────────

    const handleMyMysteryBox = () => {
        const userInfo = loginUser();
        if (!userInfo?.token) {
            setLoginSource('myMysteryBox');
            setActiveModal('login');
            return;
        }
        if (!campaign.campaignId) return;
        const userId = userInfo?.user?.userId;
        myMysteryBox.fetchList({ page: 1, userId, campaignId: campaign.campaignId }, locale);
        setActiveModal('myMysteryBox');
    };

    return {
        // 弹窗状态
        activeModal,
        closeModal,

        // 登录状态
        isLoggedIn,

        // 按钮 / 弹窗回调
        handleJoinNow,
        handleLoginSuccess,
        handleSnVerified,
        handleAddressSubmitted,
        handleOpenMysteryBox,
        handleMyMysteryBox,

        // domain hook 数据（供各弹窗组件消费）
        campaign,
        snVerification,
        addressSubmit,
        myMysteryBox,
        locale,
    };
};
